import "./styles.scss";
import React, { useState, useEffect, useCallback } from "react";
import { GeckoDialogService } from "@geckolabs/gecko-react-ui";
// Components
import Attendees from "./Attendees";
import Messages from "./Messages";
import {
  GeckoBtn,
  GeckoIcon,
  GeckoAlert,
} from "@geckolabs/gecko-react-ui/lib/components";
// Services
import {
  ATTENDEE_SUBSCRIBED_EVENT,
  ATTENDEE_ADDED_EVENT,
  ATTENDEE_REMOVED_EVENT,
  ATTENDEE_EJECTED_EVENT,
  MESSAGE_ADDED_EVENT,
  MESSAGE_REMOVED_EVENT,
} from "../../constants";
import { Listener, UnbindListener } from "../../services/_";
import { AdminService } from "../../services/AdminService";
import { PresenceSocketService } from "../../services/PresenceSocketService";
import { ChatUserNameService } from "../../services/ChatUserNameService";
import { __, STR_KEY } from "../../../../services/Translate";
import { ResponsiveService } from "../../services/ResponsiveService";
import { $stateParams } from "../../../../router";
import DataService from "../../services/DataService";

const ChatHeader = () => {
  return (
    <div className="live-chat-container-header">
      <h2 className="branded-text">Live Chat</h2>
      <button
        aria-label="Close Chat"
        type="button"
        className="icon-btn"
        onClick={ResponsiveService.chat.toggle}
      >
        <GeckoIcon icon="times" />
      </button>
    </div>
  );
};

const Chat = (attrs) => {
  const { hosts } = attrs;
  const [tab, setTab] = useState("messages");
  const [count, setCount] = useState(0);
  const [attendees, setAttendees] = useState([]);
  const [messages, setMessages] = useState([]);

  const fetchMessages = useCallback(() => {
    DataService.streamMessages().then((_messages) => {
      if (!_messages?.length) return;

      const dedupedMessages = [..._messages, ...messages]
        .reduce((acc, message) => {
          if (!acc.find((m) => m.uuid === message.uuid)) {
            acc.push(message);
          }
          return acc;
        }, [])
        .sort((a, b) => a.created_at - b.created_at);

      setMessages(dedupedMessages);
    });
  }, [messages]);

  // Attendee List
  useEffect(() => {
    fetchMessages();
    const updateAttendeeHandler = () => {
      const data = PresenceSocketService.channel().members;
      // Flatten members object into attendees array
      // Remove organisers
      const attendees = Object.keys(data.members)
        .map((secret) => data.members[secret])
        .filter((m) => !(m.is_admin || m.is_host));
      setAttendees(attendees);
      // Set attendee count
      setCount(attendees.length);
    };

    // Update attendee list (when it changes)
    const listeners = [
      Listener(ATTENDEE_SUBSCRIBED_EVENT, updateAttendeeHandler),
      Listener(ATTENDEE_ADDED_EVENT, updateAttendeeHandler),
      Listener(ATTENDEE_REMOVED_EVENT, updateAttendeeHandler),
    ];

    if (AdminService.isAdmin()) {
      listeners.push(
        Listener(ATTENDEE_ADDED_EVENT, AdminService.updateSecretToUuidMap)
      );
    }

    // On Destroy: Unbind all listeners
    return () => UnbindListener(listeners);
  }, []);

  // Message List
  useEffect(() => {
    // Add to message to messages array
    const messageAddedHandler = (data) => {
      setMessages([].concat(messages, [data]));
    };

    // Remove message from messages array
    const messageRemovedHandler = (data) => {
      setMessages(
        messages.map((m) => (m.uuid === data.uuid ? Object.assign(m, data) : m))
      );
    };

    // Remove message from messages array
    const attendeeEjectedHandler = (data) => {
      setMessages(
        messages.map((m) =>
          m.contact_secret === data.contact_secret ? Object.assign(m, data) : m
        )
      );
    };

    // Add message as they are received
    const listeners = [
      Listener(MESSAGE_ADDED_EVENT, messageAddedHandler),
      Listener(MESSAGE_REMOVED_EVENT, messageRemovedHandler),
      Listener(ATTENDEE_EJECTED_EVENT, attendeeEjectedHandler),
    ];

    // On Destroy: Unbind all listeners
    return () => UnbindListener(listeners);
  }, [messages]);

  const renderChat = () => {
    return (
      <>
        <div className="tab-nav">
          <ul className="list-inline d-flex justify-content-between text-center">
            <li
              className="list-inline-item"
              onClick={() => {
                setTab("messages");
              }}
              tabIndex="0"
              role="tab"
              onKeyDown={(e) => {
                if (e.key === "Enter") setTab("messages");
              }}
            >
              <span
                className={`branded-border-hover ${
                  tab === "messages" ? "active-tab branded-border" : ""
                }`}
              >
                {__(STR_KEY.CHAT_TAB_TITLE_CHAT)}
              </span>
            </li>
            <li
              className="list-inline-item"
              onClick={() => setTab("attendees")}
              tabIndex="0"
              role="tab"
              onKeyDown={(e) => {
                if (e.key === "Enter") setTab("attendees");
              }}
            >
              <span
                className={`branded-border-hover ${
                  tab === "attendees" ? "active-tab branded-border" : ""
                }`}
              >
                {__(STR_KEY.CHAT_TAB_TITLE_ATTENDEES)} ({count})
              </span>
            </li>
          </ul>
        </div>
        <ChatHeader />
        <div className="tab-container d-flex">
          {tab === "messages" ? (
            <Messages messages={messages} hosts={hosts} />
          ) : null}
          {tab === "attendees" ? <Attendees attendees={attendees} /> : null}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="live-chat-container gecko-ui-card card">
        {ChatUserNameService.storedName() ? (
          renderChat()
        ) : (
          <ChatConfirmation {...attrs} />
        )}
      </div>
    </>
  );
};

const showJoinAnonymouslyBtn = () => {
  const { account_uuid } = $stateParams;
  return !["ATMlbwIcTSr3vL5K"].includes(account_uuid);
};

export const ChatConfirmation = ({ contact }) => {
  const [username, setUsername] = useState(contact.full_name || "");
  const [error, setError] = useState(false);

  const sanatize = (string) => {
    return string.replace(/<[^>]*>/gi, "");
  };

  const handleChange = (event) => {
    const checkedName = sanatize(event.target.value);
    setUsername(checkedName);

    if (event.key === "Enter" && username) {
      startChat();
    }
  };

  const startChat = (isAnonymous = false) => {
    const canStartChat = isAnonymous || (!isAnonymous && username);
    if (canStartChat) {
      const usernameToInit = isAnonymous ? "Anonymous" : username;
      PresenceSocketService.destroy().then(() => {
        PresenceSocketService.init(usernameToInit);
        window.localStorage.setItem(
          ChatUserNameService.storageKey(),
          usernameToInit
        );
      });
      if (!AdminService.isAdmin()) {
        new GeckoDialogService().alert(
          <div style={{ wordBreak: "break-word" }}>
            {__(STR_KEY.CHAT_USERNAME_DESCRIPTION)}{" "}
            <strong>{usernameToInit}</strong>.
            <br />
            <br />
            {__(STR_KEY.CHAT_CONTACT_INFO_ACCESS_MESSAGE)}
          </div>
        );
      }
    } else {
      setError(true);
    }

    return;
  };

  const joinAnonymouslyBtn = showJoinAnonymouslyBtn() ? (
    <div style={{ marginTop: "4px", fontSize: "14px" }}>
      or{" "}
      <button
        type="button"
        className="gecko-chat-overlay-text-join-anonymously"
        onClick={startChat.bind(this, true)}
      >
        {__(STR_KEY.CHAT_JOIN_ANONYMOUSLY)}
      </button>
      .
    </div>
  ) : null;

  const errorAlert = error ? (
    <GeckoAlert>
      {__(STR_KEY.CHAT_ENTER_NAME_ALERT)}
      <br />
      {joinAnonymouslyBtn}
    </GeckoAlert>
  ) : null;

  return (
    <>
      <ChatHeader />
      <div className="gecko-chat-overlay d-flex justify-content-center align-items-center align-content-center flex-wrap">
        <div>
          <p className="gecko-chat-overlay-text">
            {__(STR_KEY.CHAT_DESCRIPTION_START)}:
          </p>

          {errorAlert}

          <div className="gecko-chat-overlay-input-wrapper">
            <input
              className="gecko-chat-overlay-input"
              type="text"
              defaultValue={username}
              onKeyUp={handleChange.bind(this)}
              maxLength="100"
              placeholder="Type your name"
            />
          </div>
          <GeckoBtn
            name={__(STR_KEY.CHAT_BTN_START)}
            id="chat-start"
            icon={"fa-comments"}
            handler={startChat}
            type={"primary"}
          />
          {joinAnonymouslyBtn}
        </div>
      </div>
    </>
  );
};

export default Chat;
